/**
 * results service — race results from Jolpica-F1 (primary) with
 * livetiming.formula1.com as the first fallback and OpenF1 as last resort.
 * -------------------------------------------------------------------------
 * Fallback chain (in order):
 *   1. Jolpica-F1 — free, unauthenticated, 1950–present. PRIMARY source.
 *   2. Livetiming — static files from livetiming.formula1.com (or a
 *      configured proxy/mirror). Session keys resolved via OpenF1 sessions.
 *      Requires LIVETIMING_BASE (and optionally LIVETIMING_AUTH_TOKEN) in
 *      .env.local unless pointed at a community mirror.
 *      Coverage: 2018-2021, 2023-2026 (Index.json + session files work).
 *      2022 and 2012-2017 blocked (403). Early 2024 paths work if
 *      guessed; official Index.json only from Spanish GP onward.
 *   3. OpenF1 — free, CORS-permissive. Last resort for race results data.
 *      Also used for weather enrichment (2023+ sensor → Open-Meteo) and
 *      session resolution (mapping year+round → livetiming keys).
 *
 * All three paths produce the same output shape so the UI never knows
 * which backend served the data.
 */

import { fetchWeather } from './weatherService.js';
import { fetchRaceResult as fetchFromLivetiming } from './liveTimingClient.js';
import {
  fetchSeasonRaces,
  fetchRaceResult as fetchJolpicaRaceResult,
  fetchQualifying,
  fetchSprint,
  parseStatus,
  driverDisplayName,
  formatTimeGap,
  driverNumber,
  gridPosition,
  combineDateTime,
  EARLIEST_SEASON,
} from './jolpicaClient.js';
import { FL_BONUS_YEARS, POINTS_TABLE } from './pointsConfig.js';

const OPENF1_BASE = 'https://api.openf1.org/v1';
const CACHE = new Map();
const TTL = 120_000;

async function openF1Get(path, { signal } = {}) {
  const cached = CACHE.get(path);
  if (cached && cached.expiresAt > Date.now()) return cached.value;

  let res = await fetch(`${OPENF1_BASE}${path}`, { signal, headers: { Accept: 'application/json' } });
  if (res.status === 429 || res.status >= 500) {
    await new Promise((r) => setTimeout(r, 500));
    res = await fetch(`${OPENF1_BASE}${path}`, { signal, headers: { Accept: 'application/json' } });
  }
  if (!res.ok) throw new Error(`OpenF1 ${res.status} at ${path}`);
  const value = await res.json();
  CACHE.set(path, { value, expiresAt: Date.now() + TTL });
  return value;
}

/* ===========================================================================
 * Public API
 * ========================================================================= */

export function fetchAvailableSeasons() {
  const current = new Date().getUTCFullYear();
  const seasons = [];
  for (let y = current; y >= EARLIEST_SEASON; y--) seasons.push(y);
  return seasons;
}

/**
 * fetchAvailableTracks — race calendar for a year, ordered by schedule date.
 * Jolpica primary; OpenF1 fallback if calendar fails.
 */
export async function fetchAvailableTracks(year, { signal } = {}) {
  try {
    const races = await fetchSeasonRaces(year, { signal });
    if (races && races.length > 0) {
      const now = Date.now();
      return races
        .map((r) => {
          const dateStart = combineDateTime(r.date, r.time);
          // Jolpica omits cancelled events from the calendar entirely,
          // so is_cancelled is always false — a season just has fewer rounds.
          const isConcluded = dateStart ? new Date(dateStart).getTime() + 4 * 3600_000 <= now : false;
          return {
            id: `jolpica-${year}-${r.round}`,
            season: year,
            round: r.round,
            circuitId: r.Circuit?.circuitId,
            circuit_short_name: shortCircuitName(r.raceName),
            circuit_full_name: r.Circuit?.circuitName ?? r.raceName,
            location: r.Circuit?.Location?.locality ?? '',
            country_code: '',
            country_name: r.Circuit?.Location?.country ?? '',
            flag: countryFlag(r.Circuit?.Location?.country),
            date_start: dateStart,
            is_cancelled: false,
            isConcluded,
            isMostRecent: false,
            hasSprint: !!r.Sprint,
            year,
            source: 'jolpica',
          };
        })
        .sort((a, b) => new Date(a.date_start) - new Date(b.date_start))
        .map(markMostRecent);
    }
  } catch (err) {
    if (err?.name === 'AbortError') throw err;
    console.warn('[Results] Jolpica calendar fetch failed, trying OpenF1 fallback', err.message);
  }

  // OpenF1 calendar fallback — reached only if Jolpica errored outright.
  const sessions = await openF1Get(`/sessions?session_name=Race&year=${year}`, { signal });
  if (!sessions || sessions.length === 0) return [];

  const now = Date.now();
  return sessions
    .sort((a, b) => new Date(a.date_start) - new Date(b.date_start))
    .map((s) => ({
      id: `openf1-${s.session_key}`,
      sessionKey: s.session_key,
      meetingKey: s.meeting_key,
      circuit_short_name: s.circuit_short_name,
      circuit_full_name: `${s.circuit_short_name} Circuit`,
      location: s.location,
      country_code: s.country_code,
      country_name: s.country_name,
      flag: countryFlag(s.country_name),
      date_start: s.date_start,
      date_end: s.date_end,
      is_cancelled: s.is_cancelled,
      isConcluded: !s.is_cancelled && new Date(s.date_end).getTime() <= now,
      isMostRecent: false,
      year: s.year,
      source: 'openf1',
    }))
    .map(markMostRecent);
}

function markMostRecent(t, i, arr) {
  const concluded = arr.filter((x) => x.isConcluded);
  if (concluded.length > 0 && t.id === concluded[concluded.length - 1].id) {
    t.isMostRecent = true;
  }
  return t;
}

/**
 * fetchRaceResults — full classification with 3-tier fallback.
 *
 * Tiers (in order):
 *   1. Jolpica — primary, requires track.round (from Jolpica calendar)
 *   2. Livetiming — requires sessionKey + meetingKey (resolved via OpenF1 if
 *      track came from Jolpica calendar and has a round)
 *   3. OpenF1 classification — last resort for 2023+ sessions
 */
/** Whole days from now until `iso`, floored at 0. Null when undated. */
function daysUntil(iso) {
  if (!iso) return null;
  const target = new Date(iso).getTime();
  if (!Number.isFinite(target)) return null;
  return Math.max(0, Math.ceil((target - Date.now()) / 86_400_000));
}

export async function fetchRaceResults(trackId, tracks, { signal } = {}) {
  const track = tracks?.find((t) => t.id === trackId);

  if (track?.is_cancelled) {
    return { _status: 'cancelled' };
  }
  if (track && !track.isConcluded) {
    // days_until is resolved here rather than in the card's render. Reading
    // the clock during render is impure — React may render the same tree
    // twice and get two answers — and this is the moment the number actually
    // refers to anyway: when the data was fetched.
    return {
      _status: 'upcoming',
      date_start: track.date_start,
      days_until: daysUntil(track.date_start),
    };
  }

  let sessionKey = track?.sessionKey;
  let meetingKey = track?.meetingKey;

  // Tier 1: Jolpica (primary) — requires a round number.
  if (track?.round) {
    try {
      const data = await fetchFromJolpica(track, { signal });
      if (data) return data;
    } catch (err) {
      if (err?.name === 'AbortError') throw err;
      console.warn('[Results] Jolpica failed, trying livetiming', err.message);
    }
  }

  // If track came from Jolpica calendar (no sessionKey/meetingKey),
  // resolve OpenF1 session keys for livetiming + OpenF1 fallback.
  if (!sessionKey && track?.round) {
    try {
      const matched = await findMatchingOpenF1Session(track, { signal });
      if (matched) {
        sessionKey = matched.session_key;
        meetingKey = matched.meeting_key;
      }
    } catch { /* non-critical */ }
  }

  // Tier 2: Livetiming — requires sessionKey + meetingKey.
  if (sessionKey && meetingKey) {
    try {
      const data = await fetchFromLivetiming(track.season, sessionKey, meetingKey, { signal });
      if (data) {
        // Enrich with weather + meeting info
        try {
          data.weather = await fetchWeather(sessionKey, track.location, track.date_start, { signal });
        } catch {
          data.weather = { air_temp_c: '-', track_temp_c: '-', humidity_pct: '-', conditions: '-', estimated: false };
        }
        try {
          const sessionInfo = await openF1Get(`/sessions?session_key=${sessionKey}`, { signal });
          if (sessionInfo?.[0]) {
            const s = sessionInfo[0];
            data.meeting_name = `${s.circuit_short_name ?? 'Unknown'} Grand Prix`;
            data.meeting_official_name = `FORMULA 1 ${(s.circuit_short_name ?? '').toUpperCase()} GRAND PRIX ${s.year ?? ''}`;
            data.location = s.location ?? '';
            data.country_name = s.country_name ?? '';
            data.date_start = s.date_start ?? '';
          }
        } catch { /* non-critical */ }
        return data;
      }
    } catch (err) {
      if (err?.name === 'AbortError') throw err;
      console.warn('[Results] Livetiming failed, trying OpenF1', err.message);
    }
  }

  // Tier 3: OpenF1 (last resort) — needs sessionKey.
  if (sessionKey) {
    const backupTrack = { ...track, sessionKey, meetingKey };
    try {
      return await fetchFromOpenF1(backupTrack, { signal });
    } catch (err) {
      if (err?.name === 'AbortError') throw err;
      console.warn('[Results] OpenF1 failed:', err.message);
    }
  }

  const hint = track.season < 2023
    ? ` Season data for ${track.season} may not be available from all sources.`
    : '';
  throw new Error(`Race data could not be loaded.${hint} Please try again.`);
}

/**
 * fetchSessionResults — Qualifying or Sprint classification.
 * Jolpica primary; no fallback path for non-race sessions currently.
 */
export async function fetchSessionResults(track, sessionType, { signal } = {}) {
  if (sessionType === 'qualifying') {
    const quali = await fetchQualifying(track.season, track.round, { signal });
    const rows = quali?.QualifyingResults ?? [];
    const results = rows.map((q) => {
      const bestTime = q.Q3 || q.Q2 || q.Q1 || '-';
      return {
        position: parseInt(q.position, 10),
        driver_number: driverNumber(q),
        driver_name: driverDisplayName(q.Driver),
        team_name: q.Constructor?.name ?? 'Unknown',
        time_gap: bestTime,
        points: 0,
        status: 'Finished',
        laps: '-',
        grid: '-',
        q1: q.Q1 ?? '-',
        q2: q.Q2 ?? '-',
        q3: q.Q3 ?? '-',
      };
    });
    return { results, session: { type: 'Qualifying' } };
  }

  if (sessionType === 'sprint') {
    const sprint = await fetchSprint(track.season, track.round, { signal });
    const rows = sprint?.SprintResults ?? [];
    const flSprintNum = rows.find((r) => r.FastestLap?.rank === '1');
    const flSprintDriverNumber = flSprintNum ? driverNumber(flSprintNum) : null;
    const sprintWinnerLaps = parseInt(rows[0]?.laps, 10);
    const results = rows.map((r) => {
      const status = parseStatus(r.status);
      const driverNum = driverNumber(r);
      return {
        position: parseInt(r.position, 10),
        driver_number: driverNum,
        driver_name: driverDisplayName(r.Driver),
        team_name: r.Constructor?.name ?? 'Unknown',
        time_gap: formatTimeGap(r, sprintWinnerLaps),
        points: parseFloat(r.points) || 0,
        status,
        laps: r.laps ? parseInt(r.laps, 10) : '-',
        grid: gridPosition(r.grid),
        hasFastestLap: flSprintDriverNumber !== null && driverNum === flSprintDriverNumber,
        season: track.season,
        flBonusActive: false,
      };
    });

    const poleEntry = rows.find((r) => r.grid === '1');
    const pole = poleEntry
      ? {
          driver_name: driverDisplayName(poleEntry.Driver),
          driver_number: driverNumber(poleEntry),
          lap_time: await sprintPoleLapTime(track, poleEntry, { signal }),
          // Which session set this grid slot. Lets the UI distinguish "no lap
          // time exists" from "we failed to fetch one" — two very different
          // statements to put in front of the user.
          lap_time_source: track.season >= SPRINT_SHOOTOUT_FROM ? 'sprint-qualifying' : 'qualifying',
        }
      : null;

    const flEntry = rows.find((r) => r.FastestLap?.rank === '1');
    const fastestLap = flEntry
      ? {
          driver_name: driverDisplayName(flEntry.Driver),
          driver_number: driverNumber(flEntry),
          lap_time: flEntry.FastestLap.Time?.time ?? '-',
        }
      : null;

    return { results, isSprint: true, pole, fastestLap, session: { type: 'Sprint' } };
  }

  return { results: [] };
}

/**
 * First season in which a dedicated session (Sprint Shootout in 2023, renamed
 * Sprint Qualifying from 2024) sets the sprint grid. Before that the sprint
 * started in Friday qualifying order.
 */
const SPRINT_SHOOTOUT_FROM = 2023;

/**
 * sprintPoleLapTime — the lap that earned the sprint pole, or null.
 *
 * This used to be the string '-', unconditionally, for every sprint ever run,
 * so the "Sprint Pole" tile always showed a driver above a dash.
 *
 * 2021–2022: the sprint grid is Friday's qualifying order, so the pole lap is
 * that driver's own qualifying time. Note it is NOT simply qualifying P1's
 * time — at São Paulo 2021 Hamilton qualified first and was excluded, handing
 * the sprint pole (and a different lap time) to Verstappen. Match on driverId.
 *
 * 2023 onwards: the grid comes from the shootout / sprint qualifying, and
 * Jolpica exposes no results endpoint for that session — only its schedule
 * slot. The lap time genuinely is not available, so return null and let the UI
 * omit the line rather than print a placeholder.
 */
async function sprintPoleLapTime(track, poleEntry, { signal } = {}) {
  if (track.season >= SPRINT_SHOOTOUT_FROM) return null;

  const driverId = poleEntry.Driver?.driverId;
  if (!driverId) return null;

  try {
    const quali = await fetchQualifying(track.season, track.round, { signal });
    const row = quali?.QualifyingResults?.find((q) => q.Driver?.driverId === driverId);
    return row ? (row.Q3 || row.Q2 || row.Q1 || null) : null;
  } catch (err) {
    if (err?.name === 'AbortError') throw err;
    return null; // non-critical decoration
  }
}

/* ===========================================================================
 * Jolpica path (Tier 1 — primary)
 * ========================================================================= */

async function fetchFromJolpica(track, { signal }) {
  const race = await fetchJolpicaRaceResult(track.season, track.round, { signal });
  if (!race || !race.Results || race.Results.length === 0) return null;

  const flDriverNum = race.Results.find((r) => r.FastestLap?.rank === '1');
  const flDriverNumber = flDriverNum ? driverNumber(flDriverNum) : null;

  // The classified winner's lap count is what "+N Laps" is measured against.
  const winnerLaps = parseInt(race.Results[0]?.laps, 10);

  const results = race.Results.map((r) => {
    const status = parseStatus(r.status);
    const driverNum = driverNumber(r);
    return {
      position: parseInt(r.position, 10),
      driver_number: driverNum,
      driver_name: driverDisplayName(r.Driver),
      team_name: r.Constructor?.name ?? 'Unknown',
      time_gap: formatTimeGap(r, winnerLaps),
      points: parseFloat(r.points) || 0,
      status,
      laps: r.laps ? parseInt(r.laps, 10) : '-',
      grid: gridPosition(r.grid),
      hasFastestLap: flDriverNumber !== null && driverNum === flDriverNumber,
      season: track.season,
      flBonusActive: !!FL_BONUS_YEARS[track.season],
    };
  });

  // Pole position: best of Q3/Q2/Q1 for the driver who qualified P1.
  let polePosition = { driver_name: '-', driver_number: 0, lap_time: '-' };
  try {
    const quali = await fetchQualifying(track.season, track.round, { signal });
    const poleEntry = quali?.QualifyingResults?.find((q) => q.position === '1');
    if (poleEntry) {
      polePosition = {
        driver_name: driverDisplayName(poleEntry.Driver),
        driver_number: driverNumber(poleEntry),
        lap_time: poleEntry.Q3 || poleEntry.Q2 || poleEntry.Q1 || '-',
      };
    }
  } catch { /* non-critical */ }

  // Fastest lap: driver with FastestLap.rank === "1".
  let fastestLap = { driver_name: '-', driver_number: 0, lap_time: '-', lap_number: '-' };
  if (flDriverNum) {
    fastestLap = {
      driver_name: driverDisplayName(flDriverNum.Driver),
      driver_number: driverNumber(flDriverNum),
      lap_time: flDriverNum.FastestLap.Time?.time ?? '-',
      lap_number: flDriverNum.FastestLap.lap ?? '-',
    };
  }

  // Weather: OpenF1 sensor data (2023+) → Open-Meteo estimation (all years).
  let openF1SessionKey = null;
  try {
    const matched = await findMatchingOpenF1Session(track, { signal });
    if (matched) openF1SessionKey = matched.session_key;
  } catch { /* non-critical */ }
  let weather;
  try {
    weather = await fetchWeather(openF1SessionKey, track.location, track.date_start, { signal });
  } catch {
    weather = { air_temp_c: '-', track_temp_c: '-', humidity_pct: '-', conditions: '-', estimated: false };
  }

  return {
    meeting_key: `${track.season}-${track.round}`,
    round: track.round,
    meeting_name: race.raceName,
    meeting_official_name: `FORMULA 1 ${race.raceName.toUpperCase()} ${track.season}`,
    location: race.Circuit?.Location?.locality ?? track.location,
    country_name: race.Circuit?.Location?.country ?? track.country_name,
    date_start: track.date_start,
    session_key: `${track.season}-${track.round}`,
    session_name: 'Race',
    session_type: 'Race',
    lap_count: results[0]?.laps ?? '-',
    weather,
    fastest_lap: fastestLap,
    pole_position: polePosition,
    results,
    _source: 'jolpica',
  };
}

/* ===========================================================================
 * OpenF1 path (Tier 3 — last resort, 2023+ only)
 * ========================================================================= */

async function fetchFromOpenF1(track, { signal }) {
  const sessionKey = track.sessionKey;
  console.log(`[Results] Fetching last-resort data for session ${sessionKey}…`);

  const drivers = await openF1Get(`/drivers?session_key=${sessionKey}`, { signal });
  if (!drivers || drivers.length === 0) return null;
  const driverMap = Object.fromEntries(drivers.map((d) => [d.driver_number, d]));

  const positions = await openF1Get(`/position?session_key=${sessionKey}`, { signal });
  if (!positions || positions.length === 0) return null;

  const latest = {};
  positions.forEach((r) => {
    const prev = latest[r.driver_number];
    if (!prev || new Date(r.date) > new Date(prev.date)) latest[r.driver_number] = r;
  });
  const classification = Object.values(latest).sort((a, b) => a.position - b.position);

  const allLaps = await openF1Get(`/laps?session_key=${sessionKey}`, { signal });

  const raceTimes = {};
  let fastestLapOverall = null;

  const lapsByDriver = {};
  allLaps.forEach((l) => {
    if (!lapsByDriver[l.driver_number]) lapsByDriver[l.driver_number] = [];
    lapsByDriver[l.driver_number].push(l);
  });

  for (const [numStr, laps] of Object.entries(lapsByDriver)) {
    const num = parseInt(numStr, 10);
    const valid = laps.filter((l) => l.lap_duration > 0);
    const total = valid.reduce((s, l) => s + l.lap_duration, 0);
    raceTimes[num] = { total, lapCount: valid.length };

    for (const lap of valid.filter((l) => !l.is_pit_out_lap)) {
      if (!fastestLapOverall || lap.lap_duration < fastestLapOverall.lap_duration) {
        fastestLapOverall = lap;
      }
    }
  }

  const winnerNum = classification[0]?.driver_number;
  const winnerTime = raceTimes[winnerNum]?.total ?? null;
  const winnerLapCount = raceTimes[winnerNum]?.lapCount ?? 0;

  const sessionInfo2 = await openF1Get(`/sessions?session_key=${sessionKey}`, { signal }).catch(() => []);
  const sessionLocation = sessionInfo2?.[0]?.location ?? '';
  const sessionDateStart = sessionInfo2?.[0]?.date_start ?? '';
  let weather;
  try {
    weather = await fetchWeather(sessionKey, sessionLocation, sessionDateStart, { signal });
  } catch {
    weather = { air_temp_c: '-', track_temp_c: '-', humidity_pct: '-', conditions: '-', estimated: false };
  }

  let polePosition = { driver_name: '-', driver_number: 0, lap_time: '-' };
  let gridPositions = {};
  try {
    const meetingKey = drivers[0]?.meeting_key;
    if (meetingKey) {
      const qualiSessions = await openF1Get(
        `/sessions?meeting_key=${meetingKey}&session_name=Qualifying`, { signal }
      );
      if (qualiSessions && qualiSessions.length > 0) {
        const qualiKey = qualiSessions[0].session_key;

        const allQualiPos = await openF1Get(`/position?session_key=${qualiKey}`, { signal });
        const latestQuali = {};
        allQualiPos.forEach((r) => {
          const prev = latestQuali[r.driver_number];
          if (!prev || new Date(r.date) > new Date(prev.date)) latestQuali[r.driver_number] = r;
        });
        Object.values(latestQuali).forEach((r) => { gridPositions[r.driver_number] = r.position; });

        const poleSitterNum = Object.entries(latestQuali)
          .find(([_, r]) => r.position === 1)?.[0];
        if (poleSitterNum) {
          const qualiLaps = await openF1Get(
            `/laps?session_key=${qualiKey}&driver_number=${poleSitterNum}`, { signal }
          );
          const validQL = qualiLaps.filter((l) => l.lap_duration > 0 && !l.is_pit_out_lap);
          const bestQL = validQL.length > 0
            ? validQL.reduce((a, b) => (a.lap_duration <= b.lap_duration ? a : b))
            : null;
          const poleDriver = driverMap[parseInt(poleSitterNum, 10)];
          polePosition = {
            driver_name: poleDriver?.full_name ?? `#${poleSitterNum}`,
            driver_number: parseInt(poleSitterNum, 10),
            lap_time: bestQL ? formatLapTime(bestQL.lap_duration) : '-',
          };
        }
      }
    }
  } catch { /* Non-critical */ }

  const results = classification.map((pos) => {
    const d = driverMap[pos.driver_number];
    const driverData = raceTimes[pos.driver_number];
    const driverLapCount = driverData?.lapCount ?? 0;
    const driverTotal = driverData?.total ?? 0;

    const status = classifyStatus(driverLapCount, winnerLapCount);
    let timeGap;

    if (pos.position === 1 && winnerTime) {
      timeGap = formatRaceTime(winnerTime);
    } else if (status === 'DNF' || status === 'DNS') {
      timeGap = status;
    } else if (status === 'Lapped' && winnerLapCount > 0) {
      const deficit = winnerLapCount - driverLapCount;
      timeGap = deficit === 1 ? '+1 Lap' : `+${deficit} Laps`;
    } else if (winnerTime && driverTotal && status === 'Finished') {
      const gap = driverTotal - winnerTime;
      timeGap = `+${gap.toFixed(3)}s`;
    } else {
      timeGap = `P${pos.position}`;
    }

    const basePts = pointsForPosition(pos.position, status);
    // FL bonus (2019–2024) requires a classified top-10 finish — see pointsConfig.js.
    const flBonus = fastestLapOverall?.driver_number === pos.driver_number
      && !!FL_BONUS_YEARS[track.season]
      && pos.position <= 10
      && status !== 'DNF' && status !== 'DNS' && status !== 'DSQ'
      ? 1 : 0;
    return {
      position: pos.position,
      driver_number: pos.driver_number,
      driver_name: d?.full_name ?? `Driver #${pos.driver_number}`,
      team_name: d?.team_name ?? 'Unknown',
      time_gap: timeGap,
      points: basePts + flBonus,
      status,
      laps: driverLapCount || '-',
      grid: gridPositions[pos.driver_number] ?? '-',
      hasFastestLap: fastestLapOverall?.driver_number === pos.driver_number,
      season: track.season,
      flBonusActive: !!FL_BONUS_YEARS[track.season],
    };
  });

  const sessionInfo = await openF1Get(`/sessions?session_key=${sessionKey}`, { signal });
  const session = sessionInfo?.[0] ?? {};

  const fastestLapDriver = fastestLapOverall ? driverMap[fastestLapOverall.driver_number] : null;

  return {
    meeting_key: session.meeting_key ?? 0,
    meeting_name: `${session.circuit_short_name ?? 'Unknown'} Grand Prix`,
    meeting_official_name: `FORMULA 1 ${(session.circuit_short_name ?? '').toUpperCase()} GRAND PRIX ${session.year ?? ''}`,
    location: session.location ?? '',
    country_name: session.country_name ?? '',
    date_start: session.date_start ?? '',
    session_key: sessionKey,
    session_name: 'Race',
    session_type: 'Race',
    lap_count: winnerLapCount || '-',
    weather,
    fastest_lap: fastestLapOverall
      ? {
          driver_name: fastestLapDriver?.full_name ?? `#${fastestLapOverall.driver_number}`,
          driver_number: fastestLapOverall.driver_number,
          lap_time: formatLapTime(fastestLapOverall.lap_duration),
          lap_number: fastestLapOverall.lap_number,
        }
      : { driver_name: '-', driver_number: 0, lap_time: '-', lap_number: '-' },
    pole_position: polePosition,
    results,
    _source: 'openf1-backup',
  };
}

/* ===========================================================================
 * Session resolution helper
 * ========================================================================= */

/**
 * findMatchingOpenF1Session — locates the OpenF1 race session that
 * corresponds to a Jolpica round (matched by date proximity). Returns null
 * if no match or if the year predates OpenF1's data (pre-2023).
 * Used by both weather enrichment and livetiming path resolution.
 */
async function findMatchingOpenF1Session(track, { signal }) {
  const year = track.season;
  if (year < 2023) return null;

  const sessions = await openF1Get(`/sessions?session_name=Race&year=${year}`, { signal });
  if (!sessions || sessions.length === 0) return null;

  const trackDate = new Date(track.date_start).getTime();
  const matched = sessions
    .map((s) => ({ ...s, dist: Math.abs(new Date(s.date_start).getTime() - trackDate) }))
    .sort((a, b) => a.dist - b.dist)[0];

  if (!matched || matched.dist > 86400000 * 3) return null;
  return matched;
}

/* ===========================================================================
 * Heuristic status classification (OpenF1 backup path)
 * ========================================================================= */

function classifyStatus(driverLaps, winnerLaps) {
  if (driverLaps === 0) return 'DNS';
  if (winnerLaps === 0) return 'Finished';

  const deficit = winnerLaps - driverLaps;
  const completionPct = driverLaps / winnerLaps;

  if (deficit === 0) return 'Finished';
  if (deficit <= 2 && completionPct >= 0.9) return 'Lapped';
  if (completionPct < 0.5) return 'DNF';
  if (deficit >= 3) return 'DNF';
  return 'Lapped';
}

/* ===========================================================================
 * Helpers
 * ========================================================================= */

function pointsForPosition(pos, status) {
  if (status === 'DNF' || status === 'DNS' || status === 'DSQ') return 0;
  return POINTS_TABLE[pos - 1] ?? 0;
}

function formatLapTime(seconds) {
  if (typeof seconds !== 'number' || seconds <= 0) return '-';
  const min = Math.floor(seconds / 60);
  const rest = seconds - min * 60;
  return `${min}:${rest.toFixed(3).padStart(6, '0')}`;
}

function formatRaceTime(totalSeconds) {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${s.toFixed(3).padStart(6, '0')}`;
  return `${m}:${s.toFixed(3).padStart(6, '0')}`;
}

/**
 * Exported so the standings view can label a driver's season history with the
 * same circuit names and flags the race list uses, rather than keeping a second
 * copy of the country→flag map that would drift out of sync.
 */
export function shortCircuitName(raceName) {
  if (!raceName) return 'Unknown';
  return raceName.replace(/\s+Grand Prix$/i, '');
}

const COUNTRY_FLAGS = {
  Australia: '🇦🇺', Bahrain: '🇧🇭', 'Saudi Arabia': '🇸🇦', Japan: '🇯🇵', China: '🇨🇳',
  USA: '🇺🇸', 'United States': '🇺🇸', Italy: '🇮🇹', Monaco: '🇲🇨', Canada: '🇨🇦', Spain: '🇪🇸',
  Austria: '🇦🇹', UK: '🇬🇧', 'United Kingdom': '🇬🇧', 'Great Britain': '🇬🇧', Belgium: '🇧🇪',
  Hungary: '🇭🇺', Netherlands: '🇳🇱', Azerbaijan: '🇦🇿', Singapore: '🇸🇬', Mexico: '🇲🇽',
  Brazil: '🇧🇷', Qatar: '🇶🇦', UAE: '🇦🇪', 'United Arab Emirates': '🇦🇪', Germany: '🇩🇪',
  France: '🇫🇷', Portugal: '🇵🇹', Russia: '🇷🇺', Turkey: '🇹🇷', India: '🇮🇳', Korea: '🇰🇷',
  Malaysia: '🇲🇾', 'South Africa': '🇿🇦', Argentina: '🇦🇷', Sweden: '🇸🇪', Morocco: '🇲🇦',
  Switzerland: '🇨🇭',
};
export function countryFlag(name) { return COUNTRY_FLAGS[name] ?? '🏁'; }
